import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../../theme/colors';
import GivelifyBottomSheet from '../../common/Modal/GivelifyBottomSheet';
import { Icon } from '../../common';
import { Text } from '../../common/Text/Text';
import {
    PushEventPayload,
    usePushEvents,
} from '../../../../../hooks/PushEvents';
import { Button } from '../../../../ui';

const defaultSubTitle = (
    <>
        This lets you pick up where you
        <br id="deep-link-modal-subtitle-line-break" />
        left off in the app
    </>
);

type EventPayload = PushEventPayload & {
    voucher?: string;
};

interface DeepLinkCopiedModalProps {
    id?: string;
    viewEvent: string;
    clickEvent: string;
    successEvent: string;
    url: string | undefined;
    open: boolean;
    title: string | JSX.Element;
    subTitle?: string | JSX.Element;
    closeEventPayload?: { eventName: string; eventPayload: EventPayload };
    viewEventPayload?: EventPayload;
    clickEventPayload: EventPayload;
    successEventPayload?: EventPayload;
    onClick: () => unknown;
    onClose?: () => void;
}

const StyledDiv = styled.div`
    text-align: center;
    .deep-link-heading,
    .deep-link-subheading {
        max-width: 295px;
        margin-left: auto;
        margin-right: auto;
    }
    .deep-link-heading {
        font-size: 18px;
        font-weight: 800;
        line-height: 24px;
        .deep-link-copied-text {
            color: ${colors.primaryGuidingViolet};
        }
    }
    .deep-link-subheading {
        font-size: 14px;
        margin-top: 8px;
        margin-bottom: 40px;
        .deep-link-copied-text-black {
            color: ${colors.black};
        }
    }
    .deep-link-hand {
        width: 100%;
        max-width: 100% !important;
        max-height: 300px !important;
        height: auto;
    }
`;

const DeepLinkCopiedModal: React.FC<DeepLinkCopiedModalProps> = ({
    id = 'deep-link-modal',
    viewEvent,
    clickEvent,
    successEvent,
    url,
    open,
    title,
    subTitle = defaultSubTitle,
    closeEventPayload,
    viewEventPayload,
    clickEventPayload,
    successEventPayload,
    onClick,
    onClose,
}) => {
    const { pushEvent } = usePushEvents();

    useEffect(() => {
        if (!open) return;
        pushEvent(viewEvent, viewEventPayload);
    }, [pushEvent, open, viewEvent, viewEventPayload]);

    const handleCopied = useCallback(() => {
        pushEvent(successEvent, successEventPayload);
        onClick();
    }, [pushEvent, successEvent, successEventPayload, onClick]);

    const handleClick = useCallback(() => {
        pushEvent(clickEvent, clickEventPayload);
        if (url) {
            handleCopied();
        }
    }, [pushEvent, clickEvent, clickEventPayload, url, handleCopied]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        if (!closeEventPayload) return;
        pushEvent(closeEventPayload.eventName, closeEventPayload.eventPayload);
    }, [onClose, closeEventPayload, pushEvent]);

    if (!open) return null;
    return (
        <GivelifyBottomSheet
            id={id}
            onClosedCallback={handleClose}
        >
            <StyledDiv id={`${id}-body`}>
                <Text
                    id={`${id}-title`}
                    variant="large"
                    className="deep-link-heading"
                >
                    {title}
                </Text>
                <Text
                    id={`${id}-subtitle`}
                    color="primarySecondaryContent"
                    className="deep-link-subheading"
                >
                    {subTitle}
                </Text>
                <Icon
                    id={`${id}-hand-illustration`}
                    name="deep-link-hand"
                    className="deep-link-hand"
                />
                <Button
                    id={`${id}-continue-button`}
                    onClick={handleClick}
                    className="w-full"
                >
                    Continue to App Store
                </Button>
            </StyledDiv>
        </GivelifyBottomSheet>
    );
};

export default DeepLinkCopiedModal;
